import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import ProgramNavDropdown from './component'
import {
  HOVER_CLOSE_DELAY_MS,
  HOVER_OPEN_DELAY_MS,
  ITEM_KINDS,
  VIEWPORT_MODES,
} from './constants'
import {
  claimActiveDropdown,
  createPanelId,
  getViewportMode,
  isModifiedClick,
  releaseActiveDropdown,
} from './helpers'

const useViewportMode = () => {
  const [viewportMode, setViewportMode] = useState(getViewportMode)

  useEffect(() => {
    const updateViewportMode = () => setViewportMode(getViewportMode())
    window.addEventListener('resize', updateViewportMode)
    return () => window.removeEventListener('resize', updateViewportMode)
  }, [])

  return viewportMode
}

/**
 * Desktop opens the menu on hover and the tab click navigates. Tablet opens
 * the menu on the first tap and navigates on the next. Mobile has no menu.
 */
const ProgramNavDropdownContainer = ({
  children,
  items,
  label,
  onNavigate,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [panelId] = useState(createPanelId)
  const wrapperRef = useRef(null)
  const tabSlotRef = useRef(null)
  const isPointerPressedRef = useRef(false)
  const isKeyboardFocusRef = useRef(false)
  const openTimeoutRef = useRef(null)
  const closeTimeoutRef = useRef(null)
  const viewportMode = useViewportMode()
  const isDesktop = viewportMode === VIEWPORT_MODES.desktop
  const hasLinks = !!items?.some(item => item.kind === ITEM_KINDS.link)
  const isEnabled = hasLinks && viewportMode !== VIEWPORT_MODES.mobile

  const clearHoverTimers = () => {
    clearTimeout(openTimeoutRef.current)
    clearTimeout(closeTimeoutRef.current)
  }

  const close = useCallback(() => {
    clearHoverTimers()
    releaseActiveDropdown(close)
    setIsOpen(false)
  }, [])

  const open = () => {
    clearHoverTimers()
    claimActiveDropdown(close)
    setIsOpen(true)
  }

  useEffect(
    () => () => {
      clearHoverTimers()
      releaseActiveDropdown(close)
    },
    [],
  )

  useEffect(() => {
    if (!isEnabled) {
      close()
    }
  }, [isEnabled])

  useEffect(() => {
    if (!isOpen) return

    const closeOnOutsidePress = event => {
      if (!wrapperRef.current?.contains(event.target)) {
        close()
      }
    }
    document.addEventListener('mousedown', closeOnOutsidePress)
    document.addEventListener('touchstart', closeOnOutsidePress)
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePress)
      document.removeEventListener('touchstart', closeOnOutsidePress)
    }
  }, [isOpen])

  if (!isEnabled) {
    return children ?? null
  }

  const handleMouseEnter = () => {
    if (!isDesktop) return

    clearHoverTimers()
    if (!isOpen) {
      openTimeoutRef.current = setTimeout(open, HOVER_OPEN_DELAY_MS)
    }
  }

  const closeUnlessKeyboardFocused = () => {
    const hasFocusInside = wrapperRef.current?.contains(document.activeElement)
    if (hasFocusInside && isKeyboardFocusRef.current) return

    close()
  }

  const handleMouseLeave = () => {
    if (!isDesktop) return

    clearHoverTimers()
    if (isOpen) {
      closeTimeoutRef.current = setTimeout(
        closeUnlessKeyboardFocused,
        HOVER_CLOSE_DELAY_MS,
      )
    }
  }

  const handleMouseDown = () => {
    isPointerPressedRef.current = true
  }

  const handleMouseUp = () => {
    isPointerPressedRef.current = false
  }

  const handleFocus = () => {
    isKeyboardFocusRef.current = !isPointerPressedRef.current
    if (isDesktop && !isOpen) {
      open()
    }
  }

  const handleBlur = event => {
    if (!wrapperRef.current?.contains(event.relatedTarget)) {
      close()
    }
  }

  const handleKeyDown = event => {
    if (event.key !== 'Escape' || !isOpen) return

    // Move focus before closing so focus is not left on an unmounted menu link.
    tabSlotRef.current?.querySelector('a, button, [tabindex]')?.focus()
    close()
  }

  const handleTabClickCapture = event => {
    if (isDesktop || isOpen || isModifiedClick(event)) return

    event.preventDefault()
    event.stopPropagation()
    open()
  }

  const handleItemClick = (event, route) => {
    if (isModifiedClick(event) || typeof onNavigate !== 'function') return

    event.preventDefault()
    close()
    onNavigate(route)
  }

  return (
    <ProgramNavDropdown
      isOpen={isOpen}
      items={items}
      label={label}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onItemClick={handleItemClick}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onTabClickCapture={handleTabClickCapture}
      panelId={panelId}
      tabSlotRef={tabSlotRef}
      wrapperRef={wrapperRef}
    >
      {children}
    </ProgramNavDropdown>
  )
}

ProgramNavDropdownContainer.propTypes = {
  children: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      kind: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      route: PropTypes.string,
      isSelected: PropTypes.bool,
    }),
  ),
  label: PropTypes.string,
  onNavigate: PropTypes.func,
}

export default ProgramNavDropdownContainer
